import React from 'react';
import { Card, Button, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FiCheck } from 'react-icons/fi';
import { saveBill, clearSaveResult } from './billParsingSlice';
import { LoadingState } from '../../utilities/constants';
import { parseUserInfo } from '../../utilities/common';

const ParsedDataView = ({ parsedData, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orgId } = parseUserInfo(useSelector((state) => state.users.user));
  const { saveStatus, saveResult } = useSelector((state) => state.billParsing);

  const { metadata, meters } = parsedData.parsedData || {};

  const isSaving = saveStatus === LoadingState.pending;

  const handleConfirmSave = async () => {
    const result = await dispatch(
      saveBill({
        orgId,
        data: {
          tempS3Key: parsedData.tempS3Key,
          fileName: parsedData.fileName,
          parsedData: {
            metadata,
            meters
          }
        }
      })
    );

    if (result.meta.requestStatus === 'fulfilled') {
      dispatch(clearSaveResult());
      onSuccess();
    }
  };

  const DataField = ({ label, value, required = false }) => (
    <div className="mb-2">
      <div className="d-flex align-items-start">
        <label className="text-muted mr-2" style={{ fontSize: '14px', minWidth: '150px' }}>
          {label}
          {required && <span className="text-danger">*</span>}:
        </label>
        <span style={{ fontSize: '14px', fontWeight: 500 }}>{value || '—'}</span>
      </div>
    </div>
  );

  const allRequiredFieldsMapped = metadata?.entityName && meters && meters.length > 0;

  return (
    <Card>
      <Card.Body>
        <h5 className="mb-1">{t('Data Extraction & Mapping')}</h5>
        <p className="text-muted mb-4" style={{ fontSize: '13px' }}>
          {t('Upload a bill and map extracted data to the system')}
        </p>

        <div>
          <DataField label={t('Entity Name')} value={metadata?.entityName} required />
          {metadata?.billNumber && <DataField label={t('Bill/Invoice Number')} value={metadata?.billNumber} />}
          {metadata?.provider && <DataField label={t('Provider')} value={metadata?.provider} />}
          <DataField label={t('Currency')} value={metadata?.currency} />
        </div>

        <h6 className="mt-4 mb-3">{t('Meters')}</h6>
        {meters && meters.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t('Meter Number')}</th>
                  <th>{t('Utility')}</th>
                  <th>{t('Period Start Date')}</th>
                  <th>{t('Period End Date')}</th>
                  <th>{t('Contribution')}</th>
                  <th>{t('Unit')}</th>
                  <th>{t('Cost')}</th>
                </tr>
              </thead>
              <tbody>
                {meters.map((meter, index) => (
                  <tr key={index}>
                    <td>{meter.meterNumber || '—'}</td>
                    <td>{meter.utility || '—'}</td>
                    <td>{meter.periodStart || '—'}</td>
                    <td>{meter.periodEnd || '—'}</td>
                    <td>{meter.contribution || '—'}</td>
                    <td>{meter.contributionUnit || '—'}</td>
                    <td>{meter.cost || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Alert variant="warning">{t('No meters found in the bill')}</Alert>
        )}

        {saveResult?.results?.errors && saveResult.results.errors.length > 0 && (
          <Alert variant="warning" className="mt-3">
            <div style={{ fontSize: '13px' }}>
              <strong>
                {t('Saved {{count}} out of {{total}} meters', {
                  count: saveResult.results.savedMeters || 0,
                  total: saveResult.results.totalMeters || 0
                })}
              </strong>
              <ul className="mb-0 mt-2">
                {saveResult.results.errors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          </Alert>
        )}

        <div className="d-flex justify-content-end mt-4">
          <Button variant="outline-secondary" onClick={onCancel} disabled={isSaving} className="mr-2">
            {t('Cancel')}
          </Button>
          <Button variant="dark" onClick={handleConfirmSave} disabled={!allRequiredFieldsMapped || isSaving}>
            {isSaving ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                {t('Saving...')}
              </>
            ) : (
              <>
                <FiCheck className="mr-2" />
                {t('Confirm & Save')}
              </>
            )}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ParsedDataView;
